import {
    HttpErrorResponse,
    HttpEvent,
    HttpHandler,
    HttpInterceptor,
    HttpInterceptorFn,
    HttpRequest,
} from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { isLicenseUpgradeRequiredError } from '@interacta-shared/data-access-error';
import { catchError, Observable, throwError } from 'rxjs';
import { LicenseUpgradeDisplayService } from '../services/license-upgrade-display.abstract-service';
import { licenseUpgradeTargetFromErrorBody } from '../utils/license-upgrade-error.util';

/**
 * Surfaces the licence-upgrade screen whenever the backend refuses an operation
 * reserved to the ADVANCED licence. Living in the HTTP layer, it also covers the
 * call sites that swallow the error in a local `catchError` before it reaches the
 * global ErrorHandler, and every area that has no proactive check yet. It always
 * rethrows so caller state (e.g. loading flags) is unaffected.
 *
 * The 403 body names the refused operation and its references. When it cannot be
 * filed as-is, `UNKNOWN_OPERATION` keeps the upgrade-request form reachable,
 * since a request cannot be filed without a code.
 */
@Injectable()
export class LicenseUpgradeInterceptor implements HttpInterceptor {
    private readonly displayService = inject(LicenseUpgradeDisplayService);

    intercept(
        request: HttpRequest<unknown>,
        next: HttpHandler,
    ): Observable<HttpEvent<unknown>> {
        return next.handle(request).pipe(
            catchError((error: unknown) => {
                if (isLicenseUpgradeRequiredError(error)) {
                    this.displayService.open(
                        licenseUpgradeTargetFromErrorBody(
                            (error as HttpErrorResponse).error,
                        ),
                    );
                }
                return throwError(() => error);
            }),
        );
    }
}

export const licenseUpgradeInterceptor: HttpInterceptorFn = (req, next) =>
    inject(LicenseUpgradeInterceptor).intercept(req, { handle: next });
